import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ArrowLeft, CheckCircle2, XCircle, Loader2, Clock, ExternalLink, FileText } from "lucide-react";
import { format } from "date-fns";
import { friendlyError } from "./friendlyError";

type WatchRule = {
  id: string;
  name: string;
  rule_kind: string;
  last_run_at: string | null;
  last_result: any;
};

type ResearchSource = {
  id: string;
  title: string | null;
  external_url: string | null;
  status: string;
  status_detail: string | null;
  word_count: number | null;
  ingested_at: string;
};

type LinkedItem = {
  item_id: string;
  research_items: { id: string; title: string; status: string; updated_at: string } | null;
};

export default function WatchRuleRuns() {
  const { id } = useParams<{ id: string }>();

  const { data: rule } = useQuery({
    queryKey: ["research_watch_rules", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("research_watch_rules")
        .select("id, name, rule_kind, last_run_at, last_result")
        .eq("id", id)
        .single();
      if (error) throw error;
      return data as WatchRule;
    },
    enabled: !!id,
    refetchInterval: 5_000,
  });

  const { data: sources, isLoading } = useQuery({
    queryKey: ["research_sources", "watch_rule", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("research_sources")
        .select("id, title, external_url, status, status_detail, word_count, ingested_at")
        .eq("watch_rule_id", id)
        .order("ingested_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return data as ResearchSource[];
    },
    enabled: !!id,
    refetchInterval: 5_000,
  });

  const sourceIds = (sources ?? []).map((s) => s.id);
  const { data: linkedItems } = useQuery({
    queryKey: ["research_item_sources", "for_sources", sourceIds],
    queryFn: async () => {
      if (!sourceIds.length) return [] as Array<LinkedItem & { source_id: string }>;
      const { data, error } = await supabase
        .from("research_item_sources")
        .select("source_id, item_id, research_items(id, title, status, updated_at)")
        .in("source_id", sourceIds);
      if (error) throw error;
      return (data ?? []) as any;
    },
    enabled: sourceIds.length > 0,
  });

  const itemBySource = new Map<string, LinkedItem["research_items"]>();
  (linkedItems ?? []).forEach((l: any) => {
    if (l.research_items) itemBySource.set(l.source_id, l.research_items);
  });

  if (!id) return <p className="text-sm text-muted-foreground">No rule ID provided.</p>;

  const lastResult = rule?.last_result;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <Button size="sm" variant="outline" asChild>
          <Link to="/partner/research/watch-rules">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to rules
          </Link>
        </Button>
        <div>
          <h2 className="text-xl font-semibold">Run history</h2>
          <p className="text-sm text-muted-foreground">
            {rule?.name ?? id} · {sources?.length ?? 0} source(s) ingested
          </p>
        </div>
      </div>

      {/* Most recent run summary from the rule itself */}
      {rule?.last_run_at && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              Most recent run · {format(new Date(rule.last_run_at), "dd/MM/yyyy HH:mm")}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-2 text-xs">
            <div className="flex flex-wrap gap-2 items-center">
              {lastResult?.ok ? (
                <Badge variant="outline" className="gap-1 text-emerald-600 border-emerald-300">
                  <CheckCircle2 className="h-3 w-3" /> Success
                </Badge>
              ) : (
                <Badge variant="outline" className="gap-1 text-destructive border-destructive/30">
                  <XCircle className="h-3 w-3" /> Failed
                </Badge>
              )}
              {typeof lastResult?.duration_ms === "number" && (
                <span className="text-muted-foreground">{lastResult.duration_ms} ms</span>
              )}
              {typeof lastResult?.sources_created === "number" && (
                <span className="text-muted-foreground">
                  {lastResult.sources_created} new source(s)
                </span>
              )}
            </div>
            {lastResult?.error && (
              <div className="border-l-2 border-destructive/40 pl-2">
                {(() => {
                  const f = friendlyError(lastResult.error);
                  return (
                    <>
                      <p className="font-medium text-destructive">{f.message}</p>
                      {f.hint && <p className="text-muted-foreground">{f.hint}</p>}
                    </>
                  );
                })()}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Ingested sources & drafts</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ingested</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Draft item</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground py-6">
                    <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading runs…
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && (!sources || sources.length === 0) && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground py-6">
                    No sources ingested yet. Trigger a run to scrape this rule's target.
                  </TableCell>
                </TableRow>
              )}
              {sources?.map((s) => {
                const item = itemBySource.get(s.id);
                return (
                  <TableRow key={s.id}>
                    <TableCell className="text-xs whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3 text-muted-foreground" />
                        {format(new Date(s.ingested_at), "dd/MM/yyyy HH:mm")}
                      </div>
                      {s.word_count != null && (
                        <div className="text-[10px] text-muted-foreground">
                          {s.word_count.toLocaleString()} words
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs max-w-md">
                      <div className="font-medium truncate">{s.title ?? "Untitled"}</div>
                      {s.external_url && (
                        <a
                          href={s.external_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:underline"
                        >
                          <ExternalLink className="h-3 w-3" />
                          {s.external_url.replace(/^https?:\/\//, "").slice(0, 60)}
                        </a>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px]">{s.status}</Badge>
                      {s.status_detail && (
                        <div className="text-[10px] text-muted-foreground mt-1">
                          {s.status_detail}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      {item ? (
                        <Link
                          to={`/partner/research/${item.id}`}
                          className="inline-flex items-center gap-1 hover:underline"
                        >
                          <FileText className="h-3 w-3" />
                          <span className="truncate max-w-[180px]">{item.title}</span>
                          <Badge variant="outline" className="ml-1 text-[10px]">{item.status}</Badge>
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
